// App.tsx
import { reloadIfStale } from "./utils/versionCheck";
import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
} from "react-router-dom";

import { TransitionGroup, CSSTransition } from "react-transition-group";

import GameDataProvider from "./Context/GameDataProvider";
import { UserProvider } from "./Context/UserContext";
import { getAccessToken } from "./Components/Utils/TokenRefresh";

import "./App.css";
import { LearningProvider } from "./Context/LearningContext";
import { MergeGameDataProvider } from "./Context/MergeGameDataProvider";
import { AuthProvider } from "./Context/AuthContext";
import { StreakProvider } from "./Context/StreakContext";
import { CharacterInventoryProvider } from "./Context/CharacterInventoryContext";

import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { PurchaseReauthProvider } from "./Context/PurchaseReauthContext";
import { AchievementToastProvider } from "./Context/AchievementToastContext";
import AchievementToast from "./Components/Partials/AchievementToast";
import { ToastProvider } from "./Context/ToastContext";
import ToastHost from "./Components/Ui/ToastHost";

import ErrorBoundary from "./Components/Utils/ErrorBoundary";
import QuizErrorBoundary from "./Components/Utils/QuizErrorBoundary";
import GameErrorBoundary from "./Components/Utils/GameErrorBoundary";

import LoadingSkeleton from "./Components/Ui/LoadingSkeleton";

// --- Lazy-loaded route-level components ---
import { lazyWithRetry } from "./utils/lazyWithRetry";

const AuthForm = lazyWithRetry(() => import("./Components/AuthForm"));
const Grid = lazyWithRetry(() => import("./Components/Grid"));
const MergeGame = lazyWithRetry(() => import("./Components/MergeGame"));
const AdminLayout = lazyWithRetry(() => import("./Components/Admin/AdminLayout"));
const AppShell = lazyWithRetry(() => import("./Components/Shell/AppShell"));
const LearnPage = lazyWithRetry(() => import("./Components/Pages/LearnPage"));
const Profile = lazyWithRetry(() => import("./Components/Profile"));
const Shop = lazyWithRetry(() => import("./Components/Shop"));
const CharacterInventory = lazyWithRetry(() => import("./Components/CharacterInventory"));
const CollectionBook = lazyWithRetry(() => import("./Components/CollectionBook"));
const BossRun = lazyWithRetry(() => import("./Components/Boss/BossRun"));
const StreakPage = lazyWithRetry(() => import("./Components/Streak/StreakPage"));
const InvitePage = lazyWithRetry(() => import("./Components/Invite/InvitePage"));
const GroupsPage = lazyWithRetry(() => import("./Components/Groups/GroupsPage"));
const GroupPage = lazyWithRetry(() => import("./Components/Groups/GroupPage"));
const JoinGroupPage = lazyWithRetry(() => import("./Components/Groups/JoinGroupPage"));
const CertificatePage = lazyWithRetry(() => import("./Components/Groups/CertificatePage"));
const RaidScreen = lazyWithRetry(() => import("./Components/Raid/RaidScreen"));
const FriendsPage = lazyWithRetry(() => import("./Components/Friends/FriendsPage"));
const FriendIslandPage = lazyWithRetry(() => import("./Components/Friends/FriendIslandPage"));
const FriendAddRoute = lazyWithRetry(() => import("./Components/Friends/FriendAddRoute"));
const DuelsPage = lazyWithRetry(() => import("./Components/Duels/DuelsPage"));
const DuelPage = lazyWithRetry(() => import("./Components/Duels/DuelPage"));
const ConnectTeacherBuddy = lazyWithRetry(() => import("./Components/TeacherBuddy/ConnectTeacherBuddy"));

const stripeKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
const stripePromise = stripeKey ? loadStripe(stripeKey) : null;

type AnimatedRoutesProps = {
  isLoggedIn: boolean;
  handleLogin: (token: string) => void;
  handleLogout: () => void;
};

function AnimatedRoutes({
  isLoggedIn,
  handleLogin,
  handleLogout,
}: AnimatedRoutesProps) {
  const location = useLocation();

  // route-scroll-reset: the transition wrapper is the scroll container, so a
  // tab switch would otherwise land mid-page.
  const routeWrapperRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    // A newer deploy went live while this tab was open: load it now, between screens.
    reloadIfStale();
    routeWrapperRef.current?.scrollTo?.({ top: 0 });
    document.getElementById("shell-main")?.scrollTo?.({ top: 0 });
    window.scrollTo({ top: 0 });
  }, [location.pathname]);
  return (
    <TransitionGroup className="route-transition-group">
      <CSSTransition
        key={location.pathname.startsWith("/admin") || location.pathname === "/login" || location.pathname === "/" ? location.pathname : "shell"}
        timeout={400}
        classNames="route-fade"
        unmountOnExit
      >
        <div className="route-transition-wrapper" ref={routeWrapperRef}>
          <React.Suspense fallback={<LoadingSkeleton variant="page" />}>
            <Routes location={location}>
              <Route
                path="/login"
                element={isLoggedIn ? <Navigate replace to="/play" /> : <AuthForm onLogin={handleLogin} />}
              />
              <Route
                path="/math"
                element={isLoggedIn ? <Navigate replace to="/play" /> : <AuthForm onLogin={handleLogin} track="math" />}
              />

              {/* TeacherBuddy "Play": signs in through the class link, signed in or not */}
              <Route path="/connect/teacherbuddy" element={<ConnectTeacherBuddy onLogin={handleLogin} />} />

              {/* A shared duel link works without an account (guest play). */}
              {!isLoggedIn && <Route path="/duel/:id" element={<DuelPage signedIn={false} />} />}

              {/* Signed-in shell: one HUD, one nav, pages inside */}
              <Route
                element={
                  isLoggedIn ? (
                    <ErrorBoundary>
                      <AppShell onLogout={handleLogout} />
                    </ErrorBoundary>
                  ) : (
                    <Navigate replace to="/login?mode=login" />
                  )
                }
              >
                <Route
                  path="/play"
                  element={
                    <GameErrorBoundary>
                      <MergeGame />
                    </GameErrorBoundary>
                  }
                />
                <Route
                  path="/learn"
                  element={
                    <QuizErrorBoundary>
                      <LearnPage />
                    </QuizErrorBoundary>
                  }
                />
                <Route
                  path="/build"
                  element={
                    <QuizErrorBoundary>
                      <Grid onLogout={handleLogout} />
                    </QuizErrorBoundary>
                  }
                />
                <Route path="/lunas" element={<div className="shell-page"><CharacterInventory /></div>} />
                <Route path="/collection" element={<div className="shell-page"><CollectionBook /></div>} />
                <Route path="/shop" element={<div className="shell-page"><Shop /></div>} />
                <Route path="/review" element={<Navigate replace to="/learn?tab=review" />} />
                <Route
                  path="/boss"
                  element={
                    <QuizErrorBoundary>
                      <BossRun />
                    </QuizErrorBoundary>
                  }
                />
                <Route path="/streak" element={<StreakPage />} />
                <Route path="/invite" element={<div className="shell-page"><InvitePage /></div>} />
                <Route path="/groups" element={<GroupsPage />} />
                <Route path="/groups/:id" element={<GroupPage />} />
                <Route
                  path="/raid/:groupId"
                  element={
                    <QuizErrorBoundary>
                      <RaidScreen />
                    </QuizErrorBoundary>
                  }
                />
                <Route path="/friends" element={<FriendsPage />} />
                <Route
                  path="/friends/:friendId/island"
                  element={
                    <QuizErrorBoundary>
                      <FriendIslandPage />
                    </QuizErrorBoundary>
                  }
                />
                <Route path="/duels" element={<DuelsPage />} />
                {isLoggedIn && <Route path="/duel/:id" element={<div className="shell-page"><DuelPage signedIn /></div>} />}
                <Route path="/profile" element={<div className="shell-page"><Profile onLogout={handleLogout} /></div>} />
              </Route>

              {/* Invite links work signed out too; certificates print without the shell */}
              <Route path="/groups/join/:code" element={<JoinGroupPage isLoggedIn={isLoggedIn} />} />
              <Route
                path="/groups/:id/certificate/:userId"
                element={isLoggedIn ? <CertificatePage /> : <Navigate replace to="/login?mode=login" />}
              />

              <Route
                path="/admin/*"
                element={isLoggedIn ? <AdminLayout /> : <Navigate replace to="/login?mode=login" />}
              />

              {/* The "add me" friend link works signed in or out */}
              <Route path="/friends/add/:code" element={<FriendAddRoute isLoggedIn={isLoggedIn} />} />

              {/* Legacy routes */}
              <Route path="/dashboard" element={<Navigate replace to="/learn" />} />
              <Route path="/moonrock" element={<Navigate replace to="/play" />} />
              <Route path="/game" element={<Navigate replace to="/build" />} />
              <Route
                path="/"
                element={isLoggedIn ? <Navigate replace to="/play" /> : <AuthForm onLogin={handleLogin} />}
              />
              <Route path="*" element={<Navigate replace to="/play" />} />
            </Routes>
          </React.Suspense>
        </div>
      </CSSTransition>
    </TransitionGroup>
  );
}

function App() {
  // Start from what localStorage says so a refresh on /moonrock or /game does
  // not bounce the player to the dashboard while the token is being verified.
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(() => {
    try {
      return !!localStorage.getItem("accessToken");
    } catch {
      return false;
    }
  });
  const [authChecked, setAuthChecked] = useState(false);
  // A key that changes every time the user logs in/out.
  const [userKey, setUserKey] = useState(Date.now());

  useEffect(() => {
    verifyToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifyToken = async () => {
    try {
      const accessToken = await getAccessToken();
      setIsLoggedIn(!!accessToken);
    } catch (error) {
      console.error("Error verifying token:", error);
      setIsLoggedIn(false);
    } finally {
      setAuthChecked(true);
    }
  };

  const handleLogin = (_token: string) => {
    setIsLoggedIn(true);
    // Remount the data providers so they fetch with the new token.
    setUserKey(Date.now());
  };

  const handleLogout = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    setIsLoggedIn(false);
    // Force a remount (which resets provider state)
    setUserKey(Date.now());
  };

  return (
    <div className="App">
      <header className="App-header">
        <ErrorBoundary>
          <Router>
            <Elements stripe={stripePromise}>
              <ToastProvider>
              <AuthProvider key={userKey}>
                <UserProvider key={userKey}>
                  <StreakProvider key={userKey}>
                    <CharacterInventoryProvider key={userKey}>
                      <GameDataProvider key={userKey}>
                        <MergeGameDataProvider key={userKey}>
                          <LearningProvider key={userKey}>
                            <PurchaseReauthProvider key={userKey}>
                              <AchievementToastProvider>
                                {authChecked ? (
                                  <AnimatedRoutes
                                    isLoggedIn={isLoggedIn}
                                    handleLogin={handleLogin}
                                    handleLogout={handleLogout}
                                  />
                                ) : (
                                  <LoadingSkeleton variant="page" />
                                )}
                                <AchievementToast />
                                <ToastHost />
                              </AchievementToastProvider>
                            </PurchaseReauthProvider>
                          </LearningProvider>
                        </MergeGameDataProvider>
                      </GameDataProvider>
                    </CharacterInventoryProvider>
                  </StreakProvider>
                </UserProvider>
              </AuthProvider>
              </ToastProvider>
            </Elements>
          </Router>
        </ErrorBoundary>
      </header>
    </div>
  );
}

export default App;
